import fetchAllBrands from '@dehaat/kisan-app-bl/api/fetchAllBrands'
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common'
import { Brand } from '@dehaat/kisan-app-bl/models/Product'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { LAT_LNG_COOKIE_NAME, VENDOR_ID_COOKIE_NAME } from '@/constants/common'
import {
  selectCurrentVendorId,
  selectLatLng,
  selectLoadingHyperlocalData,
} from '@/store/features/locationSlice'
import fetchPrebookBrands from '@/utils/api/fetchPrebookBrands'
import { getCookieValue } from '@/utils/helper'

import { useAppSelector } from './reduxHooks'

// Brands per vendor, kept for the session so returning to the page is instant
const CACHE_TTL_MS = 5 * 60 * 1000
const brandsCache = new Map<string, { brands: Brand[]; fetchedAt: number }>()

const getCachedBrands = (key: string) => {
  const cached = brandsCache.get(key)
  if (!cached || Date.now() - cached.fetchedAt > CACHE_TTL_MS) return undefined
  return cached.brands
}

const getCookieVendorId = () => {
  const vendorId = getCookieValue(VENDOR_ID_COOKIE_NAME)
  return vendorId && vendorId !== 'null' ? vendorId : undefined
}

// Brands of the current vendor. Server-rendered brands can be built before the
// vendor cookie exists (first visit, shared links), so refetch on the client.
// While the vendor lookup runs (it waits on geocoding), start with the vendor
// from the cookie and refetch only if the resolved vendor differs.
const useVendorBrands = (initialBrands: Brand[] = [], isPrebook = false) => {
  const { locale = DEFAULT_LANG } = useRouter()
  const resolvedVendorId = useAppSelector(selectCurrentVendorId)
  const resolvedLatLng = useAppSelector(selectLatLng)
  const loadingHyperlocalData = useAppSelector(selectLoadingHyperlocalData)

  // Without a location no vendor lookup runs, and the loading flag stays on
  const hasLocation =
    typeof document !== 'undefined' &&
    Boolean(getCookieValue(LAT_LNG_COOKIE_NAME))
  const waitingForVendor = loadingHyperlocalData && hasLocation
  const vendorId = waitingForVendor
    ? typeof document !== 'undefined'
      ? getCookieVendorId()
      : undefined
    : resolvedVendorId
  // The regular brand query also takes the location; prebook does not
  const latLng = isPrebook
    ? undefined
    : resolvedLatLng ||
      (typeof document !== 'undefined'
        ? getCookieValue(LAT_LNG_COOKIE_NAME)
        : undefined)
  const cacheKey = vendorId
    ? [vendorId, locale, isPrebook ? 'prebook' : latLng].join('|')
    : undefined

  const [brands, setBrands] = useState(initialBrands)
  const [loadingBrands, setLoadingBrands] = useState(true)

  useEffect(() => {
    if (!vendorId || !cacheKey) return

    const cached = getCachedBrands(cacheKey)
    if (cached) {
      setBrands(cached)
      setLoadingBrands(false)
      return
    }

    let cancelled = false
    setLoadingBrands(true)
    const request = isPrebook
      ? fetchPrebookBrands(vendorId, locale, (partialBrands) => {
          if (!cancelled) setBrands(partialBrands)
        })
      : fetchAllBrands(locale, latLng, vendorId)
    request
      .then((response) => {
        brandsCache.set(cacheKey, { brands: response, fetchedAt: Date.now() })
        if (!cancelled) setBrands(response)
      })
      .catch(() => {
        if (!cancelled) setBrands([])
      })
      .finally(() => {
        if (!cancelled) setLoadingBrands(false)
      })

    return () => {
      cancelled = true
    }
  }, [cacheKey, vendorId, latLng, locale, isPrebook])

  // No vendor serves the location, or there is no location at all. Drops
  // brands fetched for a stale cookie vendor.
  useEffect(() => {
    if (waitingForVendor || vendorId) return
    if (hasLocation) setBrands([])
    setLoadingBrands(false)
  }, [waitingForVendor, vendorId, hasLocation])

  return { brands, loadingBrands }
}

export default useVendorBrands
